import React from 'react'
import { motion } from 'framer-motion'
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  Music, 
  Video,
  Shield,
  AlertTriangle,
  Lock,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { gameUIVariants } from '@/lib/animations'
import { getMediaType, formatDuration } from '@/lib/media-utils'
import { LicenseManager } from './LicenseManager'

interface DRMPlayerProps {
  file: File
  cosmkId: string
  config?: any
  className?: string
  userInfo?: any
  sessionInfo?: any
  onPlayerReady?: (player: any) => void
  onPlayStateChange?: (isPlaying: boolean) => void
  onTimeUpdate?: (currentTime: number, duration?: number) => void
  onMetadataLoad?: (metadata: any) => void
  onError?: (error: any) => void
  drmState: {
    status: 'initializing' | 'ready' | 'error' | 'blocked'
    supportedSystems: string[]
    activeDRM?: string
    error?: string
  }
  licenseInfo?: any
}

export const DRMPlayer: React.FC<DRMPlayerProps> = ({
  file,
  cosmkId,
  config = {},
  className = '',
  userInfo,
  sessionInfo,
  onPlayerReady,
  onPlayStateChange,
  onTimeUpdate,
  onMetadataLoad,
  onError,
  drmState,
  licenseInfo
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [playerState, setPlayerState] = React.useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: false,
    isLoading: true,
    error: null as any,
    metadata: null as any
  })

  const [licenseStatus, setLicenseStatus] = React.useState<'pending' | 'acquired' | 'failed'>('pending')
  const [drmInitialized, setDrmInitialized] = React.useState(false)
  const mediaType = getMediaType(file)
  const isVideo = mediaType === 'video'

  // Initialize DRM and EME
  React.useEffect(() => {
    if (drmState.status === 'ready' && videoRef.current && !drmInitialized) {
      initializeEME()
    }
  }, [drmState.status, drmInitialized])

  const initializeEME = async () => {
    if (!videoRef.current) return

    try {
      console.log('Initializing DRM for COSMK ID:', cosmkId)
      setPlayerState(prev => ({ ...prev, isLoading: true }))

      // Detect supported DRM system
      const drmSystem = await detectSupportedDRM()
      if (!drmSystem) {
        throw new Error('No supported DRM system found')
      }

      console.log(`Using DRM system: ${drmSystem}`)

      // Initialize EME
      await setupEME(drmSystem)
      
      // Acquire license
      await acquireLicense(drmSystem)
      
      setDrmInitialized(true)
      setPlayerState(prev => ({ ...prev, isLoading: false }))
      
      if (onPlayerReady) {
        onPlayerReady({
          play: () => videoRef.current?.play(),
          pause: () => videoRef.current?.pause(),
          seek: (time: number) => {
            if (videoRef.current) videoRef.current.currentTime = time
          },
          setVolume: (volume: number) => {
            if (videoRef.current) videoRef.current.volume = volume
          },
          stop: () => {
            if (videoRef.current) {
              videoRef.current.pause()
              videoRef.current.currentTime = 0
            }
          }
        })
      }

    } catch (error) {
      console.error('DRM initialization failed:', error)
      setPlayerState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: { code: 'DRM_INIT_FAILED', message: error.message } 
      }))
      if (onError) onError(error)
    }
  }

  const detectSupportedDRM = async (): Promise<string | null> => {
    const userAgent = navigator.userAgent

    // Try Widevine first (most common)
    if (await testWidevinSupport()) {
      return 'widevine'
    }

    // Try FairPlay for Safari
    if (userAgent.includes('Safari') && await testFairPlaySupport()) {
      return 'fairplay'
    }

    // Try PlayReady for Edge/Windows
    if (userAgent.includes('Edge') && await testPlayReadySupport()) {
      return 'playready'
    }

    return null
  }

  const testWidevinSupport = async (): Promise<boolean> => {
    try {
      const capabilities = await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }]
      }])
      return !!capabilities
    } catch {
      return false
    }
  }

  const testFairPlaySupport = async (): Promise<boolean> => {
    try {
      // @ts-ignore - FairPlay specific API
      return !!window.WebKitMediaKeys
    } catch {
      return false
    }
  }

  const testPlayReadySupport = async (): Promise<boolean> => {
    try {
      const capabilities = await navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }]
      }])
      return !!capabilities
    } catch {
      return false
    }
  }

  const setupEME = async (drmSystem: string) => {
    if (!videoRef.current) throw new Error('Video element not available')

    const video = videoRef.current

    switch (drmSystem) {
      case 'widevine':
        await setupWidevine(video)
        break
      case 'fairplay':
        await setupFairPlay(video)
        break
      case 'playready':
        await setupPlayReady(video)
        break
      default:
        throw new Error(`Unsupported DRM system: ${drmSystem}`)
    }
  }

  const setupWidevine = async (video: HTMLVideoElement) => {
    try {
      const keySystemAccess = await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }]
      }])

      const mediaKeys = await keySystemAccess.createMediaKeys()
      await video.setMediaKeys(mediaKeys)

      const keySession = mediaKeys.createSession()
      
      keySession.addEventListener('message', async (event) => {
        try {
          const response = await fetch('/api/drm/license', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cosmkId,
              drmSystem: 'widevine',
              message: Array.from(new Uint8Array(event.message)),
              userInfo,
              sessionInfo
            })
          })

          if (!response.ok) throw new Error('License request failed')
          
          const licenseData = await response.arrayBuffer()
          await keySession.update(licenseData)
          setLicenseStatus('acquired')
        } catch (error) {
          console.error('Widevine license acquisition failed:', error)
          setLicenseStatus('failed')
        }
      })

      // Initialize with PSSH data
      const initData = new Uint8Array([]) // In production, get from manifest
      await keySession.generateRequest('cenc', initData)

    } catch (error) {
      throw new Error(`Widevine setup failed: ${error.message}`)
    }
  }

  const setupFairPlay = async (video: HTMLVideoElement) => {
    try {
      // @ts-ignore - FairPlay specific API
      if (!window.WebKitMediaKeys) {
        throw new Error('FairPlay not supported')
      }

      // @ts-ignore
      const keySystem = new WebKitMediaKeys('com.apple.fps.1_0')
      ;(video as any).webkitSetMediaKeys(keySystem)

      // @ts-ignore
      video.addEventListener('webkitneedkey', async (event) => {
        try {
          const keySession = keySystem.createSession('video/mp4', (event as any).initData)
          
          keySession.addEventListener('webkitkeymessage', async (messageEvent) => {
            const response = await fetch('/api/drm/license', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                cosmkId,
                drmSystem: 'fairplay',
                message: Array.from(new Uint8Array(messageEvent.message)),
                userInfo,
                sessionInfo
              })
            })

            if (!response.ok) throw new Error('FairPlay license request failed')
            
            const licenseData = await response.arrayBuffer()
            keySession.update(new Uint8Array(licenseData))
            setLicenseStatus('acquired')
          })

        } catch (error) {
          console.error('FairPlay license acquisition failed:', error)
          setLicenseStatus('failed')
        }
      })

    } catch (error) {
      throw new Error(`FairPlay setup failed: ${error.message}`)
    }
  }

  const setupPlayReady = async (video: HTMLVideoElement) => {
    try {
      const keySystemAccess = await navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ contentType: 'audio/mp4; codecs="mp4a.40.2"' }],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }]
      }])

      const mediaKeys = await keySystemAccess.createMediaKeys()
      await video.setMediaKeys(mediaKeys)

      const keySession = mediaKeys.createSession()
      
      keySession.addEventListener('message', async (event) => {
        try {
          const response = await fetch('/api/drm/license', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cosmkId,
              drmSystem: 'playready',
              message: Array.from(new Uint8Array(event.message)),
              userInfo,
              sessionInfo
            })
          })

          if (!response.ok) throw new Error('PlayReady license request failed')
          
          const licenseData = await response.arrayBuffer()
          await keySession.update(licenseData)
          setLicenseStatus('acquired')
        } catch (error) {
          console.error('PlayReady license acquisition failed:', error)
          setLicenseStatus('failed')
        }
      })

      // Initialize with PSSH data
      const initData = new Uint8Array([]) // In production, get from manifest
      await keySession.generateRequest('cenc', initData)

    } catch (error) {
      throw new Error(`PlayReady setup failed: ${error.message}`)
    }
  }

  const acquireLicense = async (drmSystem: string) => {
    // License acquisition is handled in the EME event handlers
    // This is a placeholder for additional license logic
    console.log(`Acquiring ${drmSystem} license for content: ${cosmkId}`)
  }

  // Event handlers
  const handlePlay = () => {
    if (videoRef.current && licenseStatus === 'acquired') {
      videoRef.current.play()
    }
  }

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause()
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef.current && onTimeUpdate) {
      onTimeUpdate(videoRef.current.currentTime)
      setPlayerState(prev => ({ ...prev, currentTime: videoRef.current!.currentTime }))
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const duration = videoRef.current.duration
      setPlayerState(prev => ({ ...prev, duration }))
      
      if (onMetadataLoad) {
        onMetadataLoad({
          duration,
          videoWidth: videoRef.current.videoWidth,
          videoHeight: videoRef.current.videoHeight
        })
      }
    }
  }

  const getDRMStatusIcon = () => {
    switch (drmState.status) {
      case 'ready':
        return <CheckCircle className="h-4 w-4 text-green-400" />
      case 'error':
      case 'blocked':
        return <XCircle className="h-4 w-4 text-red-400" />
      default:
        return <Lock className="h-4 w-4 text-yellow-400 animate-pulse" />
    }
  }

  const getLicenseStatusIcon = () => {
    switch (licenseStatus) {
      case 'acquired':
        return <CheckCircle className="h-4 w-4 text-green-400" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />
      default:
        return <Lock className="h-4 w-4 text-yellow-400 animate-pulse" />
    }
  }

  return (
    <motion.div 
      className={`bg-cosmic-dark/80 backdrop-blur-md border-2 border-stellar-cyan/30 rounded-lg p-6 ${className}`}
      variants={gameUIVariants}
      initial="elementEnter"
      animate="elementVisible"
    >
      {/* DRM Status Header */}
      <div className="flex items-center justify-between mb-4 p-3 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20">
        <div className="flex items-center space-x-3">
          <Shield className="h-5 w-5 text-stellar-cyan" />
          <div>
            <h4 className="text-sm font-semibold text-white">DRM Protected Content</h4>
            <p className="text-xs text-stellar-cyan/60">
              System: {drmState.activeDRM || 'Detecting...'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            {getDRMStatusIcon()}
            <span className="text-xs text-stellar-cyan/80">DRM</span>
          </div>
          <div className="flex items-center space-x-1">
            {getLicenseStatusIcon()}
            <span className="text-xs text-stellar-cyan/80">License</span>
          </div>
        </div>
      </div>

      {/* Video Container */}
      {isVideo && (
        <div className="relative mb-6">
          <div className="w-full bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 overflow-hidden">
            <video 
              ref={videoRef}
              className="w-full h-auto max-h-96 object-contain"
              onPlay={() => {
                setPlayerState(prev => ({ ...prev, isPlaying: true }))
                onPlayStateChange?.(true)
              }}
              onPause={() => {
                setPlayerState(prev => ({ ...prev, isPlaying: false }))
                onPlayStateChange?.(false)
              }}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onError={(e) => {
                const error = { code: 'PLAYBACK_ERROR', message: 'Video playback failed' }
                setPlayerState(prev => ({ ...prev, error }))
                onError?.(error)
              }}
              controls={false} // Use custom controls for DRM
              playsInline
              crossOrigin="anonymous"
            />
            
            {/* DRM Loading Overlay */}
            {(playerState.isLoading || licenseStatus === 'pending') && (
              <div className="absolute inset-0 flex items-center justify-center bg-cosmic-dark/80 rounded-lg">
                <div className="text-center">
                  <motion.div 
                    className="animate-spin rounded-full h-8 w-8 border-b-2 border-stellar-blue mx-auto mb-2"
                    variants={gameUIVariants}
                    animate="loadingRotate"
                  />
                  <div className="text-stellar-cyan text-sm">
                    {licenseStatus === 'pending' ? 'Acquiring DRM License...' : 'Initializing DRM...'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* License Manager */}
      <LicenseManager 
        cosmkId={cosmkId}
        drmState={drmState}
        licenseInfo={licenseInfo}
        userInfo={userInfo}
      />

      {/* Media Info */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-2 flex justify-center lg:justify-start">
          <div className="w-20 h-20 lg:w-16 lg:h-16 rounded-lg bg-gradient-to-br from-stellar-blue to-stellar-cyan flex items-center justify-center border-2 border-stellar-cyan/30">
            {isVideo ? <Video className="h-8 w-8 text-cosmic-black" /> : <Music className="h-8 w-8 text-cosmic-black" />}
          </div>
        </div>
        
        <div className="lg:col-span-7 text-center lg:text-left">
          <h3 className="text-lg lg:text-xl font-display font-semibold text-white truncate">
            {file.name}
          </h3>
          <p className="text-sm text-stellar-cyan font-mono mt-1">
            DRM Protected • {file.type.split('/')[1]?.toUpperCase()}
          </p>
          <p className="text-xs text-stellar-cyan/60 font-mono mt-1">
            COSMK ID: {cosmkId.substring(0, 16)}...
          </p>
        </div>
        
        <div className="lg:col-span-3 text-center lg:text-right">
          <p className="text-lg font-mono text-white font-semibold">
            {formatDuration(playerState.currentTime)}
          </p>
          <p className="text-sm text-stellar-cyan/60 font-mono">
            / {formatDuration(playerState.duration)}
          </p>
        </div>
      </div>

      {/* Custom DRM Controls */}
      <div className="flex items-center justify-center space-x-4">
        <motion.button
          onClick={handlePause}
          disabled={licenseStatus !== 'acquired'}
          className="w-8 h-8 text-stellar-cyan hover:text-white transition-colors disabled:opacity-50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <SkipBack className="h-5 w-5" />
        </motion.button>

        <motion.button
          onClick={playerState.isPlaying ? handlePause : handlePlay}
          disabled={licenseStatus !== 'acquired'}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            licenseStatus !== 'acquired'
              ? 'bg-stellar-gray/50 text-stellar-gray cursor-not-allowed' 
              : 'bg-stellar-blue text-cosmic-black hover:bg-stellar-cyan'
          }`}
          whileHover={licenseStatus === 'acquired' ? { scale: 1.1 } : {}}
          whileTap={licenseStatus === 'acquired' ? { scale: 0.9 } : {}}
        >
          {playerState.isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </motion.button>

        <motion.button
          onClick={handlePause}
          disabled={licenseStatus !== 'acquired'}
          className="w-8 h-8 text-stellar-cyan hover:text-white transition-colors disabled:opacity-50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <SkipForward className="h-5 w-5" />
        </motion.button>
      </div>

      {/* Error Display */}
      {(playerState.error || drmState.error) && (
        <motion.div 
          className="mt-4 p-3 bg-danger-red/20 border border-danger-red rounded-lg"
          variants={gameUIVariants}
          initial="elementEnter"
          animate="elementVisible"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-danger-red" />
            <p className="text-danger-red text-sm font-mono">
              <strong>DRM Error:</strong> {playerState.error?.message || drmState.error}
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}